import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl, FormattedMessage } from 'react-intl';
import Fuse from 'fuse.js';
import ErrorWithCountdown from '#/shared/components/ErrorWithCountdown';
import RouteList from '#/transit-tracker/components/RouteList';
import QueryInput from '#/transit-tracker/components/QueryInput';
import ROUTES from '#/constants/routes';
import useNavigate from '#/shared/hooks/useNavigate';
import useViewMapControls from '#/map/hooks/useViewMapControls';
import { routeLinkClicked } from '#/map/slice';
import DetailViewLayout from '#/components/DetailViewLayout';
import DetailNavHeader from '#/shared/components/DetailNavHeader';
import SEO from '#/shared/components/SEO';
import GTM_HANDLES from '#/shared/gtm-handles';
import { useGetRouteBasicsQuery } from '#/services/developerApi';
import { routeLookupToSortedList } from '#/transit-tracker/utils/routes';
import { AERIAL_TRAM_ROUTE_ID } from '#/transit-tracker/constants';

function RouteListContainer() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { navigate, generateNavigateBack, generateNavigateClose } =
    useNavigate();
  const { routeListViewOpened, viewClosed } = useViewMapControls();

  useEffect(() => {
    routeListViewOpened();
    return viewClosed;
  }, [routeListViewOpened, viewClosed]);

  const navigateToRoute = id => {
    dispatch(routeLinkClicked());
    navigate(ROUTES.routeById, id);
  };

  const navigateToRouteAlerts = id => navigate(ROUTES.alertsById, id);

  const { data: routeLookup, isError, isLoading } = useGetRouteBasicsQuery();
  const baseRouteList = useMemo(
    () =>
      routeLookupToSortedList(routeLookup).filter(
        // since the aerial tram only has two stops detailed information
        // about that route is not displayed in the app
        r => r.id !== AERIAL_TRAM_ROUTE_ID
      ),
    [routeLookup]
  );

  const [filteredRouteList, setFilteredRouteList] = useState(null);
  const onQueryTermChange = queryTerm => {
    const routesFuse = new Fuse(baseRouteList, {
      keys: [
        'shortName', // text on route badge
        'vanityName', // public facing route name
        'desc', // usually shortName (if any) + vanityName
      ],
      threshold: 0.25,
    });

    const trimmedTerm = queryTerm.trim();
    setFilteredRouteList(
      trimmedTerm
        ? routesFuse.search(trimmedTerm).map(r => r.item)
        : baseRouteList
    );
  };

  return (
    <>
      <SEO metadataId="routeList" />
      <DetailViewLayout
        top={
          <DetailNavHeader
            onBack={generateNavigateBack()}
            onClose={generateNavigateClose()}
            content={<FormattedMessage id="routes.routeslisttitle" />}
          />
        }
        bottom={
          (isError && (
            <ErrorWithCountdown errorMessageId="system-error.warning" />
          )) || (
            <>
              <QueryInput
                onQueryTermChange={onQueryTermChange}
                dataGtm={GTM_HANDLES.routeList.routeSearchInput}
                placeholder={intl.formatMessage({ id: 'routes.search' })}
              />
              <RouteList
                routes={
                  Array.isArray(filteredRouteList)
                    ? filteredRouteList
                    : baseRouteList
                }
                navigateToRoute={navigateToRoute}
                navigateToRouteAlerts={navigateToRouteAlerts}
                defaultMessage={
                  isLoading
                    ? intl.formatMessage({ id: 'routes.route-list-loading' })
                    : undefined
                }
              />
            </>
          )
        }
      />
    </>
  );
}

export default RouteListContainer;
